import { createContext, useContext, useEffect, useState, useRef } from 'react';
import { createClient, User, Session } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY;

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
    storage: window.localStorage,
    storageKey: 'supabase.auth.token',
    flowType: 'pkce',
  },
});

// ✅ Admin-E-Mails, die automatisch erkannt werden sollen
const ADMIN_EMAILS = [
  'info@olaf-niggemann.com',
  // Hier können weitere Admin-E-Mails hinzugefügt werden
];

interface AuthContextType {
  user: User | null;
  session: Session | null;
  tenantId: string | null;
  tenantSlug: string | null;
  userRole: string | null;
  loading: boolean;
  signUp: (email: string, password: string, companyName: string) => Promise<any>;
  signIn: (email: string, password: string) => Promise<any>;
  signInWithMagicLink: (email: string) => Promise<any>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<any>;
  updatePassword: (newPassword: string) => Promise<any>;
  refreshSession: () => Promise<Session | null>;
  forceReloadTenant: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [tenantId, setTenantId] = useState<string | null>(null);
  const [tenantSlug, setTenantSlug] = useState<string | null>(null);
  const [userRole, setUserRole] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const loadingTenantRef = useRef(false);
  const lastUserIdRef = useRef<string | null>(null);
  const tenantLoadedRef = useRef<string | null>(null);

  // ✅ Prüft, ob eine E-Mail eine Admin-E-Mail ist
  const isAdminEmail = (email: string | undefined): boolean => {
    if (!email) return false;
    return ADMIN_EMAILS.includes(email.toLowerCase());
  };

  useEffect(() => {
    console.log('🚀 AuthContext: Initializing...');
    
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      console.log('🔐 Initial Session:', session ? `✅ ${session.user.email}` : '❌ No session');
      
      if (session?.user) {
        setUser(session.user);
        setSession(session);
        // Set loading to false immediately, load tenant in background
        setLoading(false);
        loadUserTenant(session.user.id, session.user.email);
      } else {
        setLoading(false);
      }
    });

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log('🔄 Auth State Change:', event, session ? `✅ ${session.user.email}` : '❌ No session');

      // Handle sign out
      if (event === 'SIGNED_OUT') {
        console.log('👋 User signed out');
        setUser(null);
        setSession(null);
        setTenantId(null);
        setTenantSlug(null);
        setUserRole(null);
        setLoading(false);
        lastUserIdRef.current = null;
        tenantLoadedRef.current = null;
        loadingTenantRef.current = false;
        return;
      }

      // Handle sign in
      if (session?.user) {
        // Skip if we already loaded tenant for this user
        if (tenantLoadedRef.current === session.user.id) {
          console.log('⏭️ Tenant already loaded for this user, skipping...');
          setUser(session.user);
          setSession(session);
          return;
        }

        console.log('✅ Setting user:', session.user.email);
        setUser(session.user);
        setSession(session);
        // Don't block - set loading to false and load tenant in background
        setLoading(false);
        loadUserTenant(session.user.id, session.user.email);
      } else {
        setUser(null);
        setSession(null);
        setTenantId(null);
        setTenantSlug(null);
        setUserRole(null);
        setLoading(false);
      }
    });

    return () => {
      console.log('🔌 AuthContext: Cleaning up subscription');
      subscription.unsubscribe();
    };
  }, []);

  const loadUserTenant = async (userId: string, userEmail: string | undefined) => {
    // Prevent concurrent loading
    if (loadingTenantRef.current) {
      console.log('⏭️ Tenant loading already in progress, skipping...');
      return;
    }

    // Skip if already loaded for this user
    if (tenantLoadedRef.current === userId) {
      console.log('⏭️ Tenant already loaded for user:', userId);
      return;
    }

    loadingTenantRef.current = true;
    console.log('🔍 Loading Tenant for User:', userId, userEmail);

    // ✅ Prüfe, ob dies ein Admin ist
    const isAdmin = isAdminEmail(userEmail);
    if (isAdmin) {
      console.log('👑 Admin detected:', userEmail);
    }

    // Reduced timeout to 10 seconds
    const timeoutId = setTimeout(() => {
      console.warn('⏰ Tenant loading timeout - continuing without tenant data');
      loadingTenantRef.current = false;
    }, 10000);

    try {
      await performTenantLoad(userId, userEmail, timeoutId, isAdmin);
    } catch (error) {
      console.error('❌ Exception during tenant loading:', error);
      clearTimeout(timeoutId);
      loadingTenantRef.current = false;
    }
  };

  const performTenantLoad = async (
    userId: string, 
    userEmail: string | undefined, 
    timeoutId: NodeJS.Timeout,
    isAdmin: boolean
  ) => {
    console.log('📡 Step 1: Querying tenant_users table...');
    
    // ✅ Fixed query - removed 'email' column that doesn't exist
    const { data: tenantUserData, error: tenantUserError } = await supabase
      .from('tenant_users')
      .select('tenant_id, role')
      .eq('user_id', userId)
      .maybeSingle();

    console.log('📡 Query Result:', { tenantUserData, tenantUserError });

    // ✅ If no tenant found AND it's an admin - CREATE FIRST
    if ((!tenantUserData || tenantUserError) && isAdmin && userEmail) {
      console.log('👑 No tenant found for admin - creating automatically...');
      
      try {
        // Call the create-admin-tenant Edge Function
        const { data: sessionData } = await supabase.auth.getSession();
        const token = sessionData?.session?.access_token;

        if (!token) {
          console.error('❌ No session token available');
          clearTimeout(timeoutId);
          loadingTenantRef.current = false;
          return;
        }

        const response = await fetch(
          `${supabaseUrl}/functions/v1/create-admin-tenant`,
          {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          }
        );

        const result = await response.json();
        console.log('📡 Admin Tenant Creation Result:', result);

        if (result.tenant_id) {
          console.log('✅ Admin tenant created/found:', result.tenant_id);
          
          // Wait a moment for database replication
          await new Promise(resolve => setTimeout(resolve, 500));
          
          // Reload tenant data - removed 'email' from select
          const { data: newTenantUserData } = await supabase
            .from('tenant_users')
            .select('tenant_id, role')
            .eq('user_id', userId)
            .maybeSingle();

          if (newTenantUserData) {
            const { data: newTenantData } = await supabase
              .from('tenants')
              .select('id, slug, name')
              .eq('id', newTenantUserData.tenant_id)
              .single();

            if (newTenantData) {
              console.log('✅ Admin tenant loaded successfully after creation');
              setTenantId(newTenantData.id);
              setTenantSlug(newTenantData.slug);
              setUserRole(newTenantUserData.role);
              tenantLoadedRef.current = userId;
              clearTimeout(timeoutId);
              loadingTenantRef.current = false;
              return;
            }
          }
        }
      } catch (error) {
        console.error('❌ Error creating admin tenant:', error);
      }
      
      clearTimeout(timeoutId);
      loadingTenantRef.current = false;
      return;
    }

    // ✅ If tenant_user found, get the tenant details separately
    if (!tenantUserError && tenantUserData) {
      console.log('📡 Step 2: Querying tenants table...');
      
      const { data: tenantData, error: tenantError } = await supabase
        .from('tenants')
        .select('id, slug, name')
        .eq('id', tenantUserData.tenant_id)
        .single();

      if (!tenantError && tenantData) {
        const tenantInfo = {
          tenantId: tenantData.id,
          slug: tenantData.slug,
          name: tenantData.name,
          role: tenantUserData.role,
        };

        console.log('✅ Tenant loaded successfully:', tenantInfo);
        
        setTenantId(tenantInfo.tenantId);
        setTenantSlug(tenantInfo.slug);
        setUserRole(tenantInfo.role);
        tenantLoadedRef.current = userId;
        
        clearTimeout(timeoutId);
        loadingTenantRef.current = false;
        return;
      }
    }

    console.warn('⚠️ No tenant found for user and not an admin');
    clearTimeout(timeoutId);
    loadingTenantRef.current = false;
  };

  const refreshSession = async (): Promise<Session | null> => {
    console.log('🔄 Manual Session Refresh...');
    const { data: { session }, error } = await supabase.auth.refreshSession();
    
    if (error) {
      console.error('❌ Refresh Error:', error);
      return null;
    }
    
    if (session) {
      console.log('✅ Session refreshed:', session.user.email);
      setSession(session);
      setUser(session.user);
      return session;
    }
    
    return null;
  };

  const signIn = async (email: string, password: string) => {
    console.log('🔐 SignIn attempt for:', email);
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    
    if (error) {
      console.error('❌ SignIn error:', error);
      return { data: null, error };
    }
    
    console.log('✅ SignIn successful:', {
      userId: data.user?.id,
      email: data.user?.email,
      hasSession: !!data.session
    });
    
    return { data, error: null };
  };

  const signUp = async (email: string, password: string, tenantName: string) => {
    try {
      // Create user with auto-confirm
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/auth/callback`,
          data: {
            email_confirmed: true,
          },
        },
      });

      if (authError) return { error: authError };
      if (!authData.user) return { error: new Error('Benutzer konnte nicht erstellt werden') };

      // ✅ Prüfe, ob dies ein Admin ist
      if (isAdminEmail(email)) {
        console.log('👑 Admin sign-up detected, tenant will be created automatically');
        return { error: null };
      }

      // Create tenant
      const { data: tenantData, error: tenantError } = await supabase
        .from('tenants')
        .insert({
          name: tenantName,
          slug: tenantName.toLowerCase().replace(/[^a-z0-9]+/g, '-'),
          status: 'active',
        })
        .select()
        .single();

      if (tenantError) return { error: tenantError };

      // Link user to tenant - removed email field
      const { error: linkError } = await supabase
        .from('tenant_users')
        .insert({
          tenant_id: tenantData.id,
          user_id: authData.user.id,
          role: 'partner',
        });

      if (linkError) return { error: linkError };

      return { error: null };
    } catch (error) {
      return { error };
    }
  };

  const signInWithMagicLink = async (email: string) => {
    try {
      const { data, error } = await supabase.auth.signInWithOtp({
        email,
        options: {
          emailRedirectTo: `${window.location.origin}/auth/callback`,
        },
      });

      if (error) throw error;
      return { data, error: null };
    } catch (error: any) {
      return { data: null, error };
    }
  };

  const resetPassword = async (email: string) => {
    try {
      const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/auth/callback`,
      });

      if (error) throw error;
      return { data, error: null };
    } catch (error: any) {
      return { data: null, error };
    }
  };

  const signOut = async () => {
    console.log('🚪 Signing out...');
    await supabase.auth.signOut();
    setSession(null);
    setUser(null);
    setTenantId(null);
    setTenantSlug(null);
    setUserRole(null);
    lastUserIdRef.current = null;
    tenantLoadedRef.current = null;
    loadingTenantRef.current = false;
  };

  const updatePassword = async (newPassword: string) => {
    try {
      const { data, error } = await supabase.auth.updateUser({
        password: newPassword,
      });

      if (error) throw error;
      return { data, error: null };
    } catch (error: any) {
      return { data: null, error };
    }
  };

  const forceReloadTenant = async () => {
    console.log('🔄 Force reload tenant data...');
    
    if (!user) {
      console.warn('⚠️ No user to reload tenant for');
      return;
    }

    // Reset all caching flags
    tenantLoadedRef.current = null;
    loadingTenantRef.current = false;
    
    // Clear current tenant data
    setTenantId(null);
    setTenantSlug(null);
    setUserRole(null);
    
    // Reload tenant
    await loadUserTenant(user.id, user.email);
  };

  const value = {
    user,
    session,
    tenantId,
    tenantSlug,
    userRole,
    loading,
    signUp,
    signIn,
    signInWithMagicLink,
    signOut,
    resetPassword,
    updatePassword,
    refreshSession,
    forceReloadTenant,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
